import 'dayjs/locale/de';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { InternalLink } from '~/components/Button';
import Link from '~/components/Link';
import { DEFAULT_REGION, type Region } from '~/utils/region';

dayjs.extend(utc);
dayjs.extend(timezone);

export function formatDate(
  unixTimestamp: number,
  region: Region = DEFAULT_REGION
) {
  return dayjs(unixTimestamp * 1000)
    .tz(region.timeZone)
    .locale(region.lang)
    .format('DD MMM YY h:mmA');
}

type Author = {
  name: string;
  path: string;
  title: string;
};

type Props = {
  author: Author;
  className?: string;
  context?: 'header' | 'footer';
  modifiedDate?: number;
  publishedDate?: number;
  title: string;
};

function FeatureMetadata({
  title,
  author,
  publishedDate,
  modifiedDate,
  context
}: Props) {
  const displayDate = modifiedDate || publishedDate;
  const isUpdated = modifiedDate && modifiedDate !== publishedDate;

  return (
    <div
      className="flex w-full max-w-desktop flex-col gap-2 text-start lg:gap-4"
      style={{ alignItems: context === 'header' ? 'flex-start' : 'center' }}
    >
      <div
        className={
          context === 'header'
            ? 'text-display'
            : 'text-title-lg text-center leading-normal'
        }
      >
        {title}
      </div>
      <div className="text-caption-grey flex flex-wrap items-center justify-center gap-4">
        {displayDate && (
          <>
            <span>
              {isUpdated ? 'Updated ' : ''}
              {formatDate(displayDate)}
            </span>
            <span className="select-none">•</span>
          </>
        )}
        <Link
          to={author.path}
          external
          className="underline hover:text-gray-700"
        >
          {author.name}
        </Link>
      </div>
    </div>
  );
}

export function Header({
  title,
  author,
  publishedDate,
  modifiedDate,
  className
}: Props) {
  return (
    <div
      className={`flex w-full items-center justify-center bg-white px-4 py-10 text-black md:px-10 ${className ?? ''}`}
    >
      <FeatureMetadata
        title={title}
        author={author}
        publishedDate={publishedDate}
        modifiedDate={modifiedDate}
        context="header"
      />
    </div>
  );
}

type FooterProps = Props & { variant?: 'dark' | 'light' };

export function Footer({
  title,
  author,
  publishedDate,
  modifiedDate,
  variant = 'dark'
}: FooterProps) {
  const isDark = variant === 'dark';

  return (
    <div
      className={`flex w-full flex-col items-center gap-6 px-4 py-10 md:pb-14 md:pt-16 ${isDark ? 'bg-black text-white' : 'bg-white text-black'}`}
    >
      <FeatureMetadata
        title={title}
        author={author}
        publishedDate={publishedDate}
        modifiedDate={modifiedDate}
        context="footer"
      />
      <InternalLink
        to="#top"
        variant="outline"
        className={`mx-auto !w-auto ${isDark ? 'bg-black text-white' : 'border-black bg-white text-black'}`}
      >
        Back to top
      </InternalLink>
    </div>
  );
}
